<template>
  <div>
    <headline-container headline="Sales" :breadcrumbs="[{text: 'Sales'}]">
      <div v-if="hasPermission('admin')" class="btn-group">
        <button type="button" class="btn btn-white dropdown-toggle" data-toggle="dropdown">
          <i class="fa fa-cogs"></i> Functions <span class="caret"></span>
        </button>

        <ul class="dropdown-menu">
          <li>
            <a href="#" @click.prevent="downloadSales">
              <i class="fa fa-cloud-download-alt"></i> Download Sales
            </a>
          </li>
        </ul>
      </div>
    </headline-container>

    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <sales-dashboard-filters
          v-model="currentFilters"
          title="Filters"
          :users="users"
          :portals="portals"
          :channels="channels"
          :ad-formats="adFormats"
          @submit="filters = currentFilters"
          @reset="resetBase"
      ></sales-dashboard-filters>

      <sales-dashboard-filters
          v-model="currentCompareFilters"
          title="Compare"
          :show-compare-button="true"
          :display="compare"
          :users="users"
          :portals="portals"
          :channels="channels"
          :ad-formats="adFormats"
          @toggle-comparison="compare = !compare"
          @submit="compareFilters = currentCompareFilters"
          @reset="resetCompare"
      ></sales-dashboard-filters>

      <div class="row">
        <div class="col-xs-6">
          <sales-dashboard-ad-revenue-number
              :compare="compare"
              :filters="formattedAdFilters"
              :compare-filters="formattedAdCompareFilters"
          ></sales-dashboard-ad-revenue-number>
        </div>

        <div class="col-xs-6">
          <sales-dashboard-company-count-number
              :compare="compare"
              :filters="formattedAdFilters"
              :compare-filters="formattedAdCompareFilters"
          ></sales-dashboard-company-count-number>
        </div>

        <div class="col-xs-6">
          <sales-dashboard-number
              title="Company Subscription Revenue"
              metric="subscription_revenue"
              :format="value => formatPrice(value)"
              :compare="compare"
              :filters="formattedSubscriptionFilters"
              :compare-filters="formattedSubscriptionCompareFilters"
          ></sales-dashboard-number>
        </div>

        <div class="col-xs-6">
          <sales-dashboard-number
              title="Company Subscription Count"
              metric="subscription_period_count"
              :format="value => formatNumber(value)"
              :compare="compare"
              :filters="formattedSubscriptionFilters"
              :compare-filters="formattedSubscriptionCompareFilters"
          ></sales-dashboard-number>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-24">
          <sales-dashboard-ad-chart
              :compare="compare"
              :filters="formattedAdFilters"
              :compare-filters="formattedAdCompareFilters"
          ></sales-dashboard-ad-chart>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-24">
          <sales-dashboard-subscription-chart
              :compare="compare"
              :filters="formattedSubscriptionFilters"
              :compare-filters="formattedSubscriptionCompareFilters"
          ></sales-dashboard-subscription-chart>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-24">
          <sales-dashboard-total-chart
              :compare="compare"
              :ad-filters="formattedAdFilters"
              :compare-ad-filters="formattedAdCompareFilters"
              :subscription-filters="formattedSubscriptionFilters"
              :compare-subscription-filters="formattedSubscriptionCompareFilters"
          ></sales-dashboard-total-chart>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import download from 'downloadjs'
import moment from 'moment'
import store from '@/store'
import HeadlineContainer from '@/components/Layout/HeadlineContainer'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import SalesDashboardAdChart from '@/pages/Dashboards/Sales/SalesDashboardAdChart'
import SalesDashboardAdRevenueNumber from '@/pages/Dashboards/Sales/SalesDashboardAdRevenueNumber'
import SalesDashboardCompanyCountNumber from '@/pages/Dashboards/Sales/SalesDashboardCompanyCountNumber'
import SalesDashboardFilters from '@/pages/Dashboards/Sales/SalesDashboardFilters'
import SalesDashboardNumber from '@/pages/Dashboards/Sales/SalesDashboardNumber'
import SalesDashboardSubscriptionChart from '@/pages/Dashboards/Sales/SalesDashboardSubscriptionChart'
import SalesDashboardTotalChart from '@/pages/Dashboards/Sales/SalesDashboardTotalChart'
import Permissions from "../../../mixins/Permissions";

export default {
  components: {
    HeadlineContainer,
    LoadingContainer,
    SalesDashboardAdChart,
    SalesDashboardCompanyCountNumber,
    SalesDashboardFilters,
    SalesDashboardAdRevenueNumber,
    SalesDashboardNumber,
    SalesDashboardSubscriptionChart,
    SalesDashboardTotalChart
  },

  mixins: [Permissions],

  data() {
    return {
      loading: true,

      currentFilters: {
        user_id: [],
        portal_id: [],
        channel: [],
        ad_format: [],
        sold_from: moment().startOf('year').format('YYYY-MM-DD'),
        sold_to: moment().format('YYYY-MM-DD')
      },

      filters: {
        user_id: [],
        portal_id: [],
        channel: [],
        ad_format: [],
        sold_from: moment().startOf('year').format('YYYY-MM-DD'),
        sold_to: moment().format('YYYY-MM-DD')
      },

      currentCompareFilters: {
        user_id: [],
        portal_id: [],
        channel: [],
        ad_format: [],
        sold_from: moment().subtract(1, 'years').startOf('year').format('YYYY-MM-DD'),
        sold_to: moment().subtract(1, 'years').format('YYYY-MM-DD')
      },

      compareFilters: {
        user_id: [],
        portal_id: [],
        channel: [],
        ad_format: [],
        sold_from: moment().subtract(1, 'years').startOf('year').format('YYYY-MM-DD'),
        sold_to: moment().subtract(1, 'years').format('YYYY-MM-DD')
      },

      compare: false,

      users: null,
      portals: null,
      channels: null,
      adFormats: null,
      shared: store
    }
  },

  mounted() {
    Promise.all([
      http.get('/api/v2/users'),
      http.get('/api/v2/portals'),
      http.get('/api/v1/channels'),
      http.get('/api/v2/ad-formats'),
    ]).then(responses => {
      this.users = responses[0].data

      this.portals = responses[1].data

      this.channels = responses[2].data

      this.adFormats = responses[3].data.data

      this.loading = false
    })
  },

  watch: {
    compare(value) {
      if (value) {
        let filters = {
          ...this.filters,
          company_id: null,
          sold_from: moment(this.filters.sold_from).subtract(1, 'years').format('YYYY-MM-DD'),
          sold_to: moment(this.filters.sold_to).subtract(1, 'years').format('YYYY-MM-DD'),
          published_from: this.filters.published_from ? moment(this.filters.published_from).subtract(1, 'years').format('YYYY-MM-DD') : null,
          published_to: this.filters.published_to ? moment(this.filters.published_to).subtract(1, 'years').format('YYYY-MM-DD') : null
        }

        this.currentCompareFilters = {...filters}

        this.compareFilters = {...filters}
      }
    }
  },

  computed: {
    formattedFilters() {
      let filters = [
        {dimension: 'sales_date', operator: '>=', value: this.filters.sold_from},
        {dimension: 'sales_date', operator: '<=', value: this.filters.sold_to}
      ]

      if (this.filters.user_id.length) {
        filters.push({dimension: 'user_id', operator: 'in', value: this.filters.user_id})
      }

      if (this.filters.portal_id.length) {
        filters.push({dimension: 'portal_id', operator: 'in', value: this.filters.portal_id})
      }

      return filters
    },

    formattedAdFilters() {
      let filters = this.formattedFilters.concat([])

      if (this.filters.published_from) {
        filters.push({
          dimension: 'publication_date',
          operator: '>=',
          value: this.filters.published_from
        })
      }

      if (this.filters.published_to) {
        filters.push({
          dimension: 'publication_date',
          operator: '<=',
          value: this.filters.published_to
        })
      }

      if (this.filters.channel.length) {
        filters.push({dimension: 'channel', operator: 'in', value: this.filters.channel})
      }

      if (this.filters.ad_format.length) {
        filters.push({dimension: 'ad_format', operator: 'in', value: this.filters.ad_format})
      }

      if (this.filters.company_id) {
        filters.push({dimension: 'company_id', value: this.filters.company_id})
      }

      return filters
    },

    formattedSubscriptionFilters() {
      let filters = this.formattedFilters.concat([])

      filters = filters.concat([
        {dimension: 'is_first_subscription_period', value: 1},
        {dimension: 'subscriber_type', value: 'companies'}
      ])

      if (this.filters.company_id) {
        filters.push({dimension: 'subscriber_id', value: this.filters.company_id})
      }

      return filters
    },

    formattedCompareFilters() {
      let filters = [
        {dimension: 'sales_date', operator: '>=', value: this.compareFilters.sold_from},
        {dimension: 'sales_date', operator: '<=', value: this.compareFilters.sold_to}
      ]

      if (this.compareFilters.user_id.length) {
        filters.push({dimension: 'user_id', operator: 'in', value: this.compareFilters.user_id})
      }

      if (this.compareFilters.portal_id.length) {
        filters.push({dimension: 'portal_id', operator: 'in', value: this.compareFilters.portal_id})
      }

      return filters
    },

    formattedAdCompareFilters() {
      let filters = this.formattedCompareFilters.concat([])

      if (this.compareFilters.published_from) {
        filters.push({
          dimension: 'publication_date',
          operator: '>=',
          value: this.compareFilters.published_from
        })
      }

      if (this.compareFilters.published_to) {
        filters.push({
          dimension: 'publication_date',
          operator: '<=',
          value: this.compareFilters.published_to
        })
      }

      if (this.compareFilters.channel.length) {
        filters.push({dimension: 'channel', operator: 'in', value: this.compareFilters.channel})
      }

      if (this.compareFilters.ad_format.length) {
        filters.push({dimension: 'ad_format', operator: 'in', value: this.compareFilters.ad_format})
      }

      if (this.compareFilters.company_id) {
        filters.push({dimension: 'company_id', value: this.compareFilters.company_id})
      }

      return filters
    },

    formattedSubscriptionCompareFilters() {
      let filters = this.formattedCompareFilters.concat([])

      filters = filters.concat([
        {dimension: 'is_first_subscription_period', value: 1},
        {dimension: 'subscriber_type', value: 'companies'}
      ])

      if (this.compareFilters.company_id) {
        filters.push({dimension: 'subscriber_id', value: this.compareFilters.company_id})
      }

      return filters
    }
  },

  methods: {
    formatPrice(value) {
      return this.$options.filters.price(value)
    },

    formatNumber(value) {
      return this.$options.filters.number(value)
    },

    resetBase() {
      this.currentFilters = {
        user_id: [],
        portal_id: [],
        channel: [],
        ad_format: [],
        sold_from: moment().startOf('year').format('YYYY-MM-DD'),
        sold_to: moment().format('YYYY-MM-DD')
      }

      this.filters = {
        user_id: [],
        portal_id: [],
        channel: [],
        ad_format: [],
        sold_from: moment().startOf('year').format('YYYY-MM-DD'),
        sold_to: moment().format('YYYY-MM-DD')
      }
    },

    resetCompare() {
      this.currentCompareFilters = {
        user_id: [],
        portal_id: [],
        channel: [],
        ad_format: [],
        sold_from: moment().subtract(1, 'years').startOf('year').format('YYYY-MM-DD'),
        sold_to: moment().subtract(1, 'years').format('YYYY-MM-DD')
      }

      this.compareFilters = {
        user_id: [],
        portal_id: [],
        channel: [],
        ad_format: [],
        sold_from: moment().subtract(1, 'years').startOf('year').format('YYYY-MM-DD'),
        sold_to: moment().subtract(1, 'years').format('YYYY-MM-DD')
      }
    },

    downloadSales() {
      axios.post('/api/v1/reports/sales', this.filters, {
        headers: {
          'Authorization': 'Bearer ' + this.shared.token
        },
        responseType: 'blob'
      })
          .then(response => {
            download(response.data, response.headers['content-disposition'].slice(22, -1))
          })
    }
  }
}
</script>
